import React from 'react';

import InnerTabs from '~/components/core/Layout/InnerTabs/InnerTabs';
import type { PopulatedClaim } from '~/Utils/populateClaim';

import { useClaim } from '../ClaimContainer';
import SkeletonTable from '../core/Skeletons/SkeletonTable';
import { getUserRelatedExposuresId } from '../exposures/ExposureUtils';
import { useCms } from '../hooks/useCms';
import useOrganization from '../OrganizationContext';
import type { ClaimPolymorph } from '../types/claim-types';
import type { UserModel } from '../types/user-types';

import { useCommunicationsTab } from './hooks/useCommunicationsTab';

interface CommunicationTabV3Props {
  isTabActive?: boolean;
  user: UserModel;
  claim: ClaimPolymorph;
  onClaimUpdate: () => Promise<PopulatedClaim | void>;
  showClive: boolean;
}

interface CommunicationTabV3WrapperProps {
  isTabActive?: boolean;
  showClive: boolean;
}

const CommunicationTabV3Wrapper: React.FC<CommunicationTabV3WrapperProps> = ({ isTabActive = true, showClive }) => {
  const { claim, onClaimUpdate } = useClaim({ claimRequired: true });
  const { user } = useCms();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error TS233  useOrganization() not properly typed yet
  const { isLoadingCommunicationConfiguration } = useOrganization();

  // The communication configuration decides whether the handled-status filter opens on Pending, so
  // mounting the inner component before it resolves would fire an unfiltered ids request and its
  // page-data request, then discard both and refetch with handled_status=pending.
  if (!user || !claim || isLoadingCommunicationConfiguration) {
    return isTabActive ? (
      <div className="flex h-full flex-col overflow-auto">
        <SkeletonTable rowsCount={10} columnsCount={5} maxHeight="45vh" isError={false} />
      </div>
    ) : null;
  }

  return (
    <CommunicationTabV3
      isTabActive={isTabActive}
      user={user}
      claim={claim}
      onClaimUpdate={onClaimUpdate}
      showClive={showClive}
    />
  );
};

const CommunicationTabV3: React.FC<CommunicationTabV3Props> = ({
  isTabActive = true,
  user,
  claim,
  onClaimUpdate,
  showClive,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error TS233  useOrganization() not properly typed yet
  const { organizationContactRolesDict, communicationConfiguration, isLoadingCommunicationConfiguration } =
    useOrganization();

  const initialFilteredExposuresIds = getUserRelatedExposuresId(user, claim);

  const { tabs, handleTabClick } = useCommunicationsTab({
    claimId: claim.id,
    user,
    communicationsPerPage: 10,
    removeExposuresColumn: false,
    removeAdjusterColumn: false,
    addClaimColumn: false,
    displayAdjusterFilter: true,
    displayExposuresFilter: true,
    displayHandledStatusFilter: true,
    isHandledStatusEnabled: communicationConfiguration?.is_handled_status_enabled === true,
    handledFilterDefaultsToPending: communicationConfiguration?.claim_tab_handled_filter_defaults_to_pending === true,
    isCommunicationConfigLoading: Boolean(isLoadingCommunicationConfiguration),
    initialFilteredExposuresIds,
    organizationContactRolesDict,
    onClaimUpdate,
    showClive,
  });

  return isTabActive ? (
    <>
      <div className="flex h-full flex-col overflow-auto">
        <InnerTabs tabs={tabs} onTabClick={handleTabClick} />
      </div>
    </>
  ) : null;
};

export default CommunicationTabV3Wrapper;
